import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio, Seccion } from '@/components/Card';
import { NumberField, MoneyField, TextField } from '@/components/Field';
import {
  pendientesDeVenta,
  registrarVenta,
  type PendienteDeVenta,
  type RepartoSobrante,
} from '@/db/repos/sales';
import { editarVenta, eliminarVenta } from '@/db/repos/sales';
import {
  pedidosPorPrecio,
  verPedidosDeFecha,
  type PedidosAlMismoPrecio,
} from '@/db/repos/orders';
import { verVentas, type RenglonVenta } from '@/db/repos/reports';
import { DESTINOS_SOBRANTE, type DestinoSobrante } from '@/db/types';
import { hoy, sumarDias, formatearMedio, formatearCorto, esHoy, type FechaLocal } from '@/domain/dates';
import { formatearCentavos, textoACentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';

type Vista =
  | { modo: 'dia' }
  | { modo: 'registrar'; pendiente: PendienteDeVenta }
  | { modo: 'editar'; renglon: RenglonVenta };

const DESTINOS: DestinoSobrante[] = ['sobrante', 'consumo_personal', 'merma', 'otro'];

/**
 * Registro de ventas del dia.
 *
 * Se ancla en la fecha de VENTA, no de preparacion: lo que se vende hoy pudo
 * cocinarse ayer.
 */
export function SalesPage() {
  const [fecha, setFecha] = useState<FechaLocal>(hoy());
  const [vista, setVista] = useState<Vista>({ modo: 'dia' });

  const pendientes = useLiveQuery(() => pendientesDeVenta(fecha), [fecha]);
  const ventas = useLiveQuery(() => verVentas(fecha, fecha), [fecha]);

  /**
   * Los pedidos de este dia, para saber cuanto de lo entregado ya esta pagado.
   *
   * Es la unica tabla que conoce el estado de pago: una venta no guarda quien la
   * compro. Por eso el dato se muestra rotulado como "de los pedidos" en lugar de
   * mezclarse con lo vendido.
   */
  const dia = useLiveQuery(() => verPedidosDeFecha(fecha), [fecha]);

  if (vista.modo === 'registrar') {
    return (
      <RegistrarVentaForm
        pendiente={vista.pendiente}
        fecha={fecha}
        onListo={() => setVista({ modo: 'dia' })}
        onCancelar={() => setVista({ modo: 'dia' })}
      />
    );
  }

  if (vista.modo === 'editar') {
    return (
      <EditarVentaForm
        renglon={vista.renglon}
        onListo={() => setVista({ modo: 'dia' })}
        onCancelar={() => setVista({ modo: 'dia' })}
      />
    );
  }

  const ingresoDelDia = (ventas ?? []).reduce((s, v) => s + v.venta.ingresoCentavos, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Ventas"
        descripcion={esHoy(fecha) ? 'Cuánto vendiste hoy' : formatearMedio(fecha)}
      />

      <div className="flex items-center gap-2">
        <Button variante="secundario" completo={false} onClick={() => setFecha(sumarDias(fecha, -1))}>
          ‹
        </Button>
        <p className="flex-1 text-center text-sm font-medium capitalize text-texto">
          {esHoy(fecha) ? 'Hoy' : formatearCorto(fecha)}
        </p>
        <Button variante="secundario" completo={false} onClick={() => setFecha(sumarDias(fecha, 1))}>
          ›
        </Button>
      </div>

      {!esHoy(fecha) && (
        <Button variante="fantasma" onClick={() => setFecha(hoy())}>
          Volver a hoy
        </Button>
      )}

      {ingresoDelDia > 0 && (
        <Card className="bg-superficie-suave">
          <div className="flex items-baseline justify-between gap-3">
            {/* Vendido, no cobrado: puede seguir por cobrar en Pedidos. */}
            <span className="text-sm text-texto-suave">Vendido en el día</span>
            <span className="text-2xl font-bold tabular-nums text-texto">
              {formatearCentavos(ingresoDelDia)}
            </span>
          </div>

          {/*
            CUANTO DE ESO YA ESTA PAGADO.
            Las dos cifras vienen de tablas distintas y NO se restan entre si: una
            venta no guarda quien la compro, asi que no se puede saber que parte de
            "vendido" esta pagada. Lo que si se sabe es el estado de pago de los
            PEDIDOS de este dia, y por eso se rotula de donde sale.
            Sumar o restar estos numeros daria una cifra inventada.
          */}
          {dia !== undefined && dia.resumen.cuantos > 0 && (
            <div className="mt-2 border-t border-borde pt-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
                De los pedidos de este día
              </p>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-sm text-texto-suave">Ya te pagaron</span>
                <span className="font-semibold tabular-nums text-exito">
                  {formatearCentavos(dia.resumen.cobradoCentavos)}
                </span>
              </div>
              <div className="mt-0.5 flex items-baseline justify-between gap-3">
                <span className="text-sm text-texto-suave">Te deben</span>
                <span
                  className={`text-lg font-bold tabular-nums ${
                    dia.resumen.porCobrarCentavos > 0 ? 'text-aviso' : 'text-texto'
                  }`}
                >
                  {formatearCentavos(dia.resumen.porCobrarCentavos)}
                </span>
              </div>

              {dia.resumen.porCobrarCentavos > 0 && (
                <div className="mt-3">
                  <Link to="/pedidos">
                    <Button variante="secundario">Marcar quién ya pagó</Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {pendientes === undefined && <p className="text-sm text-texto-suave">Cargando…</p>}

      {pendientes !== undefined && pendientes.length === 0 && (
        <EstadoVacio
          titulo="No hay platillos para este día"
          descripcion="Las ventas se registran sobre los platillos del menú."
        >
          <Link to="/menu">
            <Button variante="secundario">Ir al menú</Button>
          </Link>
        </EstadoVacio>
      )}

      {pendientes !== undefined && pendientes.length > 0 && (
        <Seccion titulo="Platillos del día">
          <ul className="space-y-2">
            {pendientes.map((pendiente) => (
              <li key={pendiente.platillo.id}>
                <Card className={pendiente.cerrado ? 'bg-superficie-suave' : ''}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-texto">{pendiente.nombreReceta}</p>
                      <p className="text-sm text-texto-suave">
                        {pendiente.porcionesDisponibles}{' '}
                        {pendiente.porcionesDisponibles === 1 ? 'porción' : 'porciones'} ·{' '}
                        {formatearCentavos(pendiente.platillo.precioVentaCentavos)} cada una
                      </p>
                      {pendiente.cerrado ? (
                        <p className="mt-1 text-xs text-exito">
                          ✓ {pendiente.yaVendidas} vendidas
                          {pendiente.yaAsignadas > 0 && `, ${pendiente.yaAsignadas} sin vender`}
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-aviso">
                          {pendiente.porRegistrar} sin registrar
                        </p>
                      )}
                      {pendiente.produccion === null && (
                        <p className="mt-1 text-xs text-texto-tenue">
                          Sin producción registrada: no se va a saber el costo
                        </p>
                      )}
                    </div>
                  </div>

                  {!pendiente.cerrado && (
                    <div className="mt-3">
                      <Button onClick={() => setVista({ modo: 'registrar', pendiente })}>
                        Registrar venta
                      </Button>
                    </div>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      {ventas !== undefined && ventas.length > 0 && (
        <Seccion titulo="Ya registrado">
          <ul className="space-y-2">
            {ventas.map((renglon) => (
              <li key={renglon.venta.id}>
                <Card>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-texto">{renglon.nombreReceta}</p>
                      <p className="text-sm text-texto-suave">
                        {renglon.venta.porcionesVendidas} vendidas a{' '}
                        {formatearCentavos(renglon.venta.precioUnitarioCentavos)}
                      </p>
                      {renglon.destinos.length > 0 && (
                        <p className="mt-0.5 text-xs text-texto-tenue">
                          {renglon.destinos
                            .map((d) => `${d.porciones} ${d.etiqueta.toLowerCase()}`)
                            .join(' · ')}
                        </p>
                      )}
                      {renglon.esCorreccion && (
                        <p className="mt-0.5 text-xs italic text-texto-tenue">
                          Corrige un registro anterior
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-semibold tabular-nums text-texto">
                        {formatearCentavos(renglon.venta.ingresoCentavos)}
                      </p>
                      {renglon.gananciaCentavos === null ? (
                        <p className="text-xs text-texto-tenue">costo n/d</p>
                      ) : (
                        <p className="text-xs text-texto-suave">
                          deja {formatearCentavos(renglon.gananciaCentavos)}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <Button
                      variante="fantasma"
                      completo={false}
                      onClick={() => setVista({ modo: 'editar', renglon })}
                    >
                      Corregir
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </Seccion>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsRegistrar {
  pendiente: PendienteDeVenta;
  fecha: FechaLocal;
  onListo: () => void;
  onCancelar: () => void;
}

/**
 * Captura de la venta con el destino de lo que no se vendio.
 *
 * El destino no es un adorno: sin el, el costo de las porciones que no se
 * vendieron quedaria sin dueño y el resultado del dia se veria mejor de lo real.
 */
function RegistrarVentaForm({ pendiente, fecha, onListo, onCancelar }: PropsRegistrar) {
  const disponibles = pendiente.porRegistrar;

  const [vendidas, setVendidas] = useState(String(disponibles));
  const [precio, setPrecio] = useState(
    (pendiente.platillo.precioVentaCentavos / 100).toFixed(2),
  );
  const [reparto, setReparto] = useState<Record<DestinoSobrante, string>>({
    sobrante: '',
    consumo_personal: '',
    merma: '',
    otro: '',
  });
  const [notas, setNotas] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [advertencias, setAdvertencias] = useState<string[]>([]);

  /**
   * LOS PEDIDOS DE ESTE PLATILLO, AGRUPADOS POR PRECIO.
   *
   * El formulario solo, sin esto, propone el precio del menu para todas las
   * porciones. El 31 de agosto eso daba 3 pollos x $120 = $360, cuando lo cobrado
   * fueron $340: a Juan se le respeta $100 por ser cliente de años. Y el Tablero,
   * que si lee los pedidos, decia $580 mientras Ventas llevaba a registrar $600.
   *
   * Dos pantallas con dos numeros para el mismo dia es justo lo que hay que
   * evitar, asi que aqui manda lo pedido.
   */
  const grupos = useLiveQuery(
    () => pedidosPorPrecio(pendiente.platillo.id),
    [pendiente.platillo.id],
  );

  const vendidasNumero = Number(vendidas);
  const precioCentavos = textoACentavos(precio) ?? 0;

  const porcionesPedidas = (grupos ?? []).reduce((s, g) => s + g.porciones, 0);
  const ingresoDePedidos = (grupos ?? []).reduce(
    (s, g) => s + Math.round(g.porciones * g.precioCentavos),
    0,
  );

  /**
   * Solo se ofrece si lo pedido cabe en lo que falta por registrar. Si sobra
   * comida, las porciones de mas siguen pendientes y se capturan aparte con su
   * destino: este atajo no adivina a donde fue lo que nadie pidio.
   */
  const puedeUsarPedidos = porcionesPedidas > 0 && porcionesPedidas <= disponibles;

  const asignadas = DESTINOS.reduce((s, d) => s + (Number(reparto[d]) || 0), 0);
  const sinVender = Math.max(0, disponibles - (Number.isFinite(vendidasNumero) ? vendidasNumero : 0));
  const faltaAsignar = sinVender - asignadas;

  const valida =
    Number.isFinite(vendidasNumero) && vendidasNumero >= 0 && vendidasNumero <= disponibles;

  const ingreso = valida ? Math.round(vendidasNumero * precioCentavos) : 0;

  async function guardar() {
    setGuardando(true);
    setError(null);

    const sobrantes: RepartoSobrante[] = DESTINOS.filter(
      (d) => (Number(reparto[d]) || 0) > 0,
    ).map((d) => ({ tipo: d, porciones: Number(reparto[d]) }));

    try {
      const resultado = await registrarVenta({
        plannedItemId: pendiente.platillo.id,
        porcionesVendidas: vendidasNumero,
        precioUnitarioCentavos: precioCentavos,
        fecha,
        sobrantes,
        notas: notas.trim() === '' ? null : notas,
      });

      if (resultado.advertencias.length > 0) {
        setAdvertencias(resultado.advertencias);
        setGuardando(false);
        return;
      }
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  /**
   * Registra la venta partida por precio: una captura por cada precio distinto.
   *
   * `registrarVenta()` ya contempla varias capturas del mismo platillo y reparte
   * el costo del lote sin descuadrar, porque la captura que lo cierra se lleva el
   * remanente exacto en lugar de su proporcion. Por eso van en serie y no en
   * paralelo: cada una necesita ver lo que registro la anterior.
   */
  async function guardarSegunPedidos(renglones: PedidosAlMismoPrecio[]) {
    setGuardando(true);
    setError(null);

    try {
      let ultimas: string[] = [];

      for (const renglon of renglones) {
        // Quien pidio, para que la venta no pierda el rastro de a quien se le
        // entrego. Se recorta porque el campo no admite mas de 200 caracteres.
        const quienes = renglon.clientes.join(', ').slice(0, 200);

        const resultado = await registrarVenta({
          plannedItemId: pendiente.platillo.id,
          porcionesVendidas: renglon.porciones,
          precioUnitarioCentavos: renglon.precioCentavos,
          fecha,
          sobrantes: [],
          notas: quienes === '' ? null : quienes,
        });

        /**
         * Solo se guardan los avisos de la ULTIMA captura. Las intermedias avisan
         * "quedan N porciones sin registrar", que es cierto en ese instante y deja
         * de serlo en la siguiente vuelta. Mostrarlos alarmaria sin motivo.
         */
        ultimas = resultado.advertencias;
      }

      if (ultimas.length > 0) {
        setAdvertencias(ultimas);
        setGuardando(false);
        return;
      }
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        titulo={pendiente.nombreReceta}
        descripcion={`${disponibles} ${
          disponibles === 1 ? 'porción' : 'porciones'
        } por registrar · ${formatearMedio(fecha)}`}
      />

      {grupos !== undefined && grupos.length > 0 && (
        <Seccion
          titulo="Lo que está pedido"
          descripcion="Cada quien con el precio que le tocó, para que el ingreso salga como lo vas a cobrar"
        >
          <Card className="bg-superficie-suave">
            <ul className="space-y-1">
              {grupos.map((grupo) => (
                <li
                  key={grupo.precioCentavos}
                  className="flex items-baseline justify-between gap-3"
                >
                  <span className="min-w-0 break-words text-sm text-texto">
                    {grupo.clientes.join(', ')}
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-sm tabular-nums text-texto-suave">
                    {grupo.porciones} × {formatearCentavos(grupo.precioCentavos)}
                  </span>
                </li>
              ))}
            </ul>

            <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-borde pt-2">
              <span className="text-sm text-texto-suave">
                {porcionesPedidas} {porcionesPedidas === 1 ? 'porción pedida' : 'porciones pedidas'}
              </span>
              <span className="text-2xl font-bold tabular-nums text-marca-fuerte">
                {formatearCentavos(ingresoDePedidos)}
              </span>
            </div>
          </Card>

          {puedeUsarPedidos ? (
            <>
              <Button
                tamano="grande"
                disabled={guardando}
                onClick={() => void guardarSegunPedidos(grupos)}
              >
                {guardando
                  ? 'Guardando…'
                  : `Registrar así · ${formatearCentavos(ingresoDePedidos)}`}
              </Button>
              <p className="text-xs text-texto-tenue">
                {grupos.length > 1
                  ? `Se anota en ${grupos.length} partes, una por cada precio, porque una venta ` +
                    'lleva un solo precio. El costo se reparte solo.'
                  : 'Toma el precio de los pedidos, no el del menú.'}
                {porcionesPedidas < disponibles &&
                  ` Las otras ${disponibles - porcionesPedidas} quedan pendientes para decir a dónde fueron.`}
              </p>
            </>
          ) : (
            <Banner tono="aviso" titulo="Hay más pedido que comida por registrar">
              Están pedidas {porcionesPedidas} y solo quedan {disponibles} por registrar. Captura a
              mano lo que sí entregaste.
            </Banner>
          )}
        </Seccion>
      )}

      <Seccion
        titulo={grupos !== undefined && grupos.length > 0 ? 'O anótalo a mano' : 'Venta'}
        descripcion={
          grupos !== undefined && grupos.length > 0
            ? 'Por si vendiste algo distinto a lo que estaba pedido'
            : undefined
        }
      >
        <NumberField
          etiqueta="Cuántas se vendieron"
          valor={vendidas}
          onCambio={setVendidas}
          decimal={false}
          autoFocus
          error={valida ? null : `Tiene que ser entre 0 y ${disponibles}`}
        />

        <MoneyField
          etiqueta="Precio por porción"
          valor={precio}
          onCambio={setPrecio}
          ayuda="Viene del menú. Cámbialo si vendiste a otro precio."
        />

        {/*
          El aviso existe porque el descuido caro es este: capturar tantas
          porciones como pedidos pero al precio de lista, y cobrar de menos sin
          notarlo. Solo aparece cuando los dos numeros deberian coincidir.
        */}
        {valida &&
          porcionesPedidas > 0 &&
          vendidasNumero === porcionesPedidas &&
          ingreso !== ingresoDePedidos && (
            <Banner tono="aviso" titulo="Ese total no es el de los pedidos">
              Estás anotando {formatearCentavos(ingreso)} y lo pedido suma{' '}
              {formatearCentavos(ingresoDePedidos)}. Si es porque a alguien le das otro precio, usa
              el botón de arriba.
            </Banner>
          )}
      </Seccion>

      {ingreso > 0 && (
        <Card className="bg-marca-suave">
          <p className="text-center text-sm text-texto-suave">Ingreso</p>
          <p className="text-center text-3xl font-bold tabular-nums text-marca-fuerte">
            {formatearCentavos(ingreso)}
          </p>
        </Card>
      )}

      {sinVender > 0 && (
        <Seccion
          titulo={`¿A dónde fueron las otras ${sinVender}?`}
          descripcion="Su costo ya lo pagaste, así que conviene saber dónde quedó"
        >
          <div className="space-y-3">
            {DESTINOS.map((destino) => (
              <NumberField
                key={destino}
                etiqueta={DESTINOS_SOBRANTE[destino]}
                valor={reparto[destino]}
                onCambio={(valor) => setReparto({ ...reparto, [destino]: valor })}
                decimal={false}
                placeholder="0"
              />
            ))}
          </div>

          {faltaAsignar > 0 && (
            <Banner tono="aviso" titulo={`Faltan ${faltaAsignar} por clasificar`}>
              Puedes guardar así, pero el costo de esas porciones va a quedar sin destino.
            </Banner>
          )}
          {faltaAsignar < 0 && (
            <Banner tono="alerta" titulo="Estás repartiendo más de las que sobran">
              Sobran {sinVender} y estás clasificando {asignadas}.
            </Banner>
          )}
        </Seccion>
      )}

      {pendiente.costoProduccionCentavos === null && (
        <Banner tono="info" titulo="Sin producción registrada">
          No cocinaste esto desde la app, así que no se conoce el costo. El ingreso sí se
          registra, y la ganancia va a aparecer como no disponible.
        </Banner>
      )}

      {pendiente.costoIncompleto && (
        <Banner tono="aviso" titulo="El costo está incompleto">
          A algún insumo de esta receta le falta precio, así que la ganancia va a ser parcial.
        </Banner>
      )}

      <TextField
        etiqueta="Nota"
        valor={notas}
        onCambio={setNotas}
        placeholder="Opcional"
        maxLength={200}
      />

      {advertencias.length > 0 && (
        <Banner tono="aviso" titulo="Se registró con avisos">
          <ul className="list-disc space-y-1 pl-4">
            {advertencias.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
          <div className="mt-3">
            <Button variante="secundario" onClick={onListo}>
              Entendido
            </Button>
          </div>
        </Banner>
      )}

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo registrar">
          {error}
        </Banner>
      )}

      {advertencias.length === 0 && (
        <div className="space-y-2">
          <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
            {guardando ? 'Guardando…' : 'Registrar venta'}
          </Button>
          <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsEditar {
  renglon: RenglonVenta;
  onListo: () => void;
  onCancelar: () => void;
}

/**
 * Corrige o elimina una venta ya registrada.
 *
 * Al guardar se recalculan el ingreso, la parte del costo que le toca a esta
 * venta y la ganancia. El inventario NO se toca, porque una venta nunca lo toco:
 * los insumos se descuentan al completar la produccion. El costo congelado de la
 * produccion tampoco cambia.
 */
function EditarVentaForm({ renglon, onListo, onCancelar }: PropsEditar) {
  const [vendidas, setVendidas] = useState(String(renglon.venta.porcionesVendidas));
  const [precio, setPrecio] = useState(
    (renglon.venta.precioUnitarioCentavos / 100).toFixed(2),
  );
  const [reparto, setReparto] = useState<Record<DestinoSobrante, string>>(() => {
    const inicial: Record<DestinoSobrante, string> = {
      sobrante: '',
      consumo_personal: '',
      merma: '',
      otro: '',
    };
    for (const destino of renglon.destinos) {
      const clave = DESTINOS.find((d) => DESTINOS_SOBRANTE[d] === destino.etiqueta);
      if (clave !== undefined) inicial[clave] = String(destino.porciones);
    }
    return inicial;
  });
  const [notas, setNotas] = useState(renglon.venta.notas ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmaBorrar, setConfirmaBorrar] = useState(false);

  const vendidasNumero = Number(vendidas);
  const precioCentavos = textoACentavos(precio) ?? 0;
  const valida = Number.isFinite(vendidasNumero) && vendidasNumero >= 0;
  const ingreso = valida ? Math.round(vendidasNumero * precioCentavos) : 0;

  async function guardar() {
    setGuardando(true);
    setError(null);

    const sobrantes: RepartoSobrante[] = DESTINOS.filter(
      (d) => (Number(reparto[d]) || 0) > 0,
    ).map((d) => ({ tipo: d, porciones: Number(reparto[d]) }));

    try {
      await editarVenta(renglon.venta.id, {
        porcionesVendidas: vendidasNumero,
        precioUnitarioCentavos: precioCentavos,
        sobrantes,
        notas: notas.trim() === '' ? null : notas,
      });
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function borrar() {
    setGuardando(true);
    setError(null);
    try {
      await eliminarVenta(renglon.venta.id);
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        titulo={`Corregir: ${renglon.nombreReceta}`}
        descripcion={renglon.fechaLegible}
      />

      <NumberField
        etiqueta="Porciones vendidas"
        valor={vendidas}
        onCambio={setVendidas}
        decimal={false}
        autoFocus
        error={valida ? null : 'Pon un número de cero en adelante'}
      />

      <MoneyField etiqueta="Precio por porción" valor={precio} onCambio={setPrecio} />

      {ingreso > 0 && (
        <Card className="bg-marca-suave">
          <p className="text-center text-sm text-texto-suave">Ingreso corregido</p>
          <p className="text-center text-3xl font-bold tabular-nums text-marca-fuerte">
            {formatearCentavos(ingreso)}
          </p>
        </Card>
      )}

      <Seccion titulo="Porciones no vendidas" descripcion="Deja en blanco lo que no aplique">
        <div className="space-y-3">
          {DESTINOS.map((destino) => (
            <NumberField
              key={destino}
              etiqueta={DESTINOS_SOBRANTE[destino]}
              valor={reparto[destino]}
              onCambio={(valor) => setReparto({ ...reparto, [destino]: valor })}
              decimal={false}
              placeholder="0"
            />
          ))}
        </div>
      </Seccion>

      <TextField etiqueta="Nota" valor={notas} onCambio={setNotas} maxLength={200} />

      <Banner tono="info" titulo="Qué se recalcula">
        Se ajustan el ingreso, el costo asignado a esta venta y la ganancia. El inventario no
        cambia, porque los insumos se descontaron cuando cocinaste. El costo de la producción
        tampoco se toca.
      </Banner>

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar corrección'}
        </Button>
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>

        {confirmaBorrar ? (
          <Card>
            <p className="text-sm text-texto">
              Se va a eliminar esta venta y sus porciones no vendidas. El ingreso deja de contar
              y las porciones quedan disponibles para registrarse de nuevo.
            </p>
            <div className="mt-3 space-y-2">
              <Button disabled={guardando} onClick={() => void borrar()}>
                Sí, eliminar la venta
              </Button>
              <Button
                variante="secundario"
                disabled={guardando}
                onClick={() => setConfirmaBorrar(false)}
              >
                No, conservarla
              </Button>
            </div>
          </Card>
        ) : (
          <Button variante="fantasma" disabled={guardando} onClick={() => setConfirmaBorrar(true)}>
            Eliminar esta venta
          </Button>
        )}
      </div>
    </div>
  );
}
